import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { Document } from '@contentful/rich-text-types';
import { Box, Col, Link, Row } from '@opendoor/bricks-next';
import { IconArrowRight } from '@tabler/icons-react';
import type { EntryComponent, IRenderContext } from 'cms/entries/entries';
import { Subhead, TitleWithAccent } from 'components/landing-pages-v2/shared/Typography';
import { Divider } from 'components/shared/Divider';
import { ILpComponentFaqDisplay, ILpComponentFaqDisplayFields } from 'declarations/contentful';
import Head from 'next/head';
import type { FAQPage, WithContext } from 'schema-dts';

import RichTextRenderer from '../../cms/renderer';
import Grid, { gridStyles } from './Grid';

const isDocument = (value: unknown): value is Document =>
  typeof value === 'object' && value !== null && (value as Document).nodeType === 'document';

const buildFaqSchema = (
  faQs: NonNullable<ILpComponentFaqDisplayFields['faQs']>,
): WithContext<FAQPage> => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faQs
    .filter((faq) => Boolean(faq?.fields))
    .map((faq) => {
      const { title, body } = faq.fields as { title?: string; body?: unknown };
      return {
        '@type': 'Question',
        name: title ?? '',
        acceptedAnswer: {
          '@type': 'Answer',
          text: isDocument(body) ? documentToPlainTextString(body) : '',
        },
      };
    }),
});

const RenderFAQsDisplay = (
  entry: ILpComponentFaqDisplay,
  _loaderProps?: unknown,
  context?: IRenderContext,
) => {
  const { fields } = entry;

  const faqSchema = fields?.faQs && fields.faQs.length > 0 ? buildFaqSchema(fields.faQs) : null;
  const entryLookup = context?.entryLookup ?? { block: {}, inline: {} };

  const viewAllLink = fields?.viewMoreLinkUrl ? (
    <Link
      aria-label="View more FAQs"
      analyticsName={`cosmos-landing-page-faqs-display-${fields?.analyticsId}`}
      href={fields?.viewMoreLinkUrl}
    >
      View all FAQ{' '}
      {<IconArrowRight size={16} style={{ color: '#0D76D6', verticalAlign: 'middle' }} />}
    </Link>
  ) : null;
  return (
    <>
      {faqSchema && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(faqSchema).replace(/</g, '\\u003c'),
            }}
          />
        </Head>
      )}
      <Col
        analyticsName={`cosmos-landing-page-faqs-display-${fields?.analyticsId}`}
        trackImpression
      >
        <Grid backgroundColor="$backgroundSecondary" py="$32x">
          <Box
            $platform-web={{ gridColumn: gridStyles.colSpan12 }}
            width="100%"
            flexDirection="column"
            $largerThanSM={{ flexDirection: 'row', pr: '$10x' }}
            $largerThanMD={{ pr: '$4x' }}
            alignContent="center"
            pr="$4x"
          >
            <Col width="100%" gap="$28x">
              <Row width="100%" justifyContent="space-between" alignItems="flex-end">
                <TitleWithAccent title={fields?.title} maxWidth="unset" mb="$0x" />
                <Col display="none" $largerThanSM={{ display: 'flex' }} pb="$4x">
                  {viewAllLink}
                </Col>
              </Row>
              <Col>
                <Col>
                  {fields?.faQs?.map((faq: any, idx) => {
                    return (
                      <Col key={idx}>
                        <Col
                          my="$20x"
                          $largerThanSM={{ my: '$10x' }}
                          $largerThanMD={{ my: '$24x' }}
                          display={idx === 0 ? 'none' : 'flex'}
                        >
                          <Divider />
                        </Col>
                        <Box
                          gap="$10x"
                          $largerThanSM={{
                            flexDirection: 'row',
                            gap: '15%',
                            alignItems: 'flex-start',
                          }}
                          $largerThanLG={{ pr: '$44x' }}
                          width="100%"
                          flexDirection="column"
                        >
                          <Col width="100%" $largerThanSM={{ width: '35%' }}>
                            <Subhead $largerThanMD={{ fontSize: 24 }}>{faq?.fields?.title}</Subhead>
                          </Col>
                          <Col width="100%" $largerThanSM={{ width: '55%', mt: -20 }}>
                            <RichTextRenderer.Component
                              body={faq?.fields?.body}
                              idToLoaderData={{}}
                              entryLookup={entryLookup}
                            />
                          </Col>
                        </Box>
                      </Col>
                    );
                  })}
                </Col>
              </Col>
              <Col $largerThanSM={{ display: 'none' }}>{viewAllLink}</Col>
            </Col>
          </Box>
        </Grid>
      </Col>
    </>
  );
};

const FAQsDisplay: EntryComponent<ILpComponentFaqDisplay> = {
  render: RenderFAQsDisplay,
};

export default FAQsDisplay;
